// Prévia de link — a consulta em lote (spec de 27/10/2026, §4.3).
//
// Uma tela com 30 mensagens com link monta 30 cartões no mesmo instante. Um pedido por link seriam 30 idas ao
// servidor (e ao site de cada um); aqui o que chega no mesmo tique (20 ms) vira UM pedido, sem repetidos, partido
// em pedidos de no máximo 10 links (o servidor aceita 20; o resto da cota é folga contra o tempo do pedido, que
// espera até ~5 s por site). Cada chamador recebe o SEU cartão, achado pelo `link` do eco: o servidor devolve a
// lista na ordem que quiser e pode omitir um link.
//
// O lote não guarda nada: quem lembra do cartão por 5 minutos é o react-query (`usePreviaDeLink`).

import {
  consultarPrevias,
  type CartaoDoSite,
  type OrigemDasPrevias,
  type RespostaDePrevias,
} from "@/services/previasDeLinkApi";

/** Links por pedido. O servidor aceita 20; 10 mantém cada pedido curto (cada link pode levar segundos). */
export const MAX_LINKS_POR_PEDIDO = 10;

/** O tique: quanto o lote espera, a contar da primeira chamada, para juntar quem chegar junto. */
export const ESPERA_DO_LOTE_MS = 20;

/** O que cada chamador recebe: o seu cartão (`null` = sem cartão) e se o interruptor está ligado. */
export interface ResultadoDaConsulta {
  cartao: CartaoDoSite | null;
  ligada: boolean;
}

export interface ConsultaEmLote {
  /** Pede o cartão de um link (o endereço normalizado, até 2048 caracteres). Rejeita se o pedido dele falhar. */
  consultar(link: string): Promise<ResultadoDaConsulta>;
}

interface Chamador {
  resolver(resultado: ResultadoDaConsulta): void;
  rejeitar(erro: unknown): void;
}

/** O lote por dentro: `descartar` é do `reiniciarLotesParaTeste` e não aparece no tipo público. */
interface LoteInterno extends ConsultaEmLote {
  descartar(): void;
}

function montarLote(
  enviar: (links: string[]) => Promise<RespostaDePrevias>,
  opcoes?: { esperaMs?: number; maximo?: number }
): LoteInterno {
  const esperaMs = opcoes?.esperaMs ?? ESPERA_DO_LOTE_MS;
  const maximo = opcoes?.maximo ?? MAX_LINKS_POR_PEDIDO;

  // link -> quem espera por ele. O mesmo link pedido duas vezes ocupa UM slot e as duas chamadas resolvem.
  let fila = new Map<string, Chamador[]>();
  let tique: ReturnType<typeof setTimeout> | null = null;

  /** Manda um pedido e entrega o resultado só a quem estava nele; a falha de um pedido não toca nos outros. */
  async function disparar(links: string[], chamadores: Map<string, Chamador[]>) {
    try {
      const resposta = await enviar(links);
      const ligada = resposta.ligada;
      const doEco = new Map(resposta.previas.map((previa) => [previa.link, previa.cartao]));
      for (const link of links) {
        // Desligado, ninguém ganha cartão (o servidor já manda tudo nulo; aqui não se confia no eco).
        const cartao = ligada ? (doEco.get(link) ?? null) : null;
        for (const chamador of chamadores.get(link) ?? []) chamador.resolver({ cartao, ligada });
      }
    } catch (erro) {
      // O erro ORIGINAL (do axios) vai a cada chamador deste pedido; quem chama decide o que fazer com ele.
      // Resolver/rejeitar uma promessa já assentada não faz nada, então uma falha no meio da entrega é segura.
      for (const link of links) {
        for (const chamador of chamadores.get(link) ?? []) chamador.rejeitar(erro);
      }
    }
  }

  function esvaziar() {
    tique = null;
    const pendentes = fila;
    fila = new Map();
    const links = Array.from(pendentes.keys());
    // Todos os pedidos do tique saem juntos: nenhum espera o outro.
    for (let inicio = 0; inicio < links.length; inicio += maximo) {
      const fatia = links.slice(inicio, inicio + maximo);
      const chamadoresDaFatia = new Map(fatia.map((link) => [link, pendentes.get(link) ?? []]));
      // `disparar` não rejeita (tem o seu `try/catch`): sem promessa solta.
      void disparar(fatia, chamadoresDaFatia);
    }
  }

  return {
    consultar(link) {
      return new Promise<ResultadoDaConsulta>((resolver, rejeitar) => {
        const chamadores = fila.get(link);
        if (chamadores) chamadores.push({ resolver, rejeitar });
        else fila.set(link, [{ resolver, rejeitar }]);
        tique ??= setTimeout(esvaziar, esperaMs);
      });
    },
    descartar() {
      if (tique !== null) clearTimeout(tique);
      tique = null;
      fila = new Map();
    },
  };
}

/**
 * Um lote próprio, com o `enviar` que o chamador der. Quem usa no app é `consultaDaOrigem`; esta é a forma
 * que os testes (e quem precisar de outro destino) usam.
 */
export function criarConsultaEmLote(
  enviar: (links: string[]) => Promise<RespostaDePrevias>,
  opcoes?: { esperaMs?: number; maximo?: number }
): ConsultaEmLote {
  return montarLote(enviar, opcoes);
}

// Um lote por origem, no módulo: o colaborador e o parceiro usam sessões diferentes (`api` e `portalApi`) e
// um pedido nunca mistura as duas.
const lotesDasOrigens = new Map<OrigemDasPrevias, LoteInterno>();

/** O lote da origem (`interno` ou `portal`): todas as telas dela, juntas, pedem pelo mesmo. */
export function consultaDaOrigem(origem: OrigemDasPrevias): ConsultaEmLote {
  let lote = lotesDasOrigens.get(origem);
  if (!lote) {
    lote = montarLote((links) => consultarPrevias(links, origem));
    lotesDasOrigens.set(origem, lote);
  }
  return lote;
}

/**
 * Só para teste: o lote é singleton de módulo, e um pedido que ainda não saiu (o tique de 20 ms) vazaria para
 * o teste seguinte. Cancela o tique, esquece a fila e os lotes. Quem estava esperando nunca é resolvido.
 */
export function reiniciarLotesParaTeste(): void {
  for (const lote of lotesDasOrigens.values()) lote.descartar();
  lotesDasOrigens.clear();
}
